/**
 * @file `SignInCard` — canonical sign-in / sign-up surface, promoted
 *       from `@labcraft/lab/ui/components/auth_card.tsx` so every web
 *       host renders the same form.
 * @module @labcraft/ui/chrome/sign-in/sign-in-card
 *
 * Single auth surface — sign-up and sign-in collapse here. The
 * backend resolves new vs returning via `findOrLinkByClaims` (see
 * ADR-006 in mono/docs/architecture). Three entry points, in
 * canonical order: GitHub → Google → Email magic link.
 *
 * Decoupled from any router or service container. The caller wires
 * three flow callbacks plus optional `onEmailSent`; see {@link
 * SignInCardProps}. The lab's `/authorize` page passes
 * `onEmailSent={(email) => navigate('/auth/sent?…')}` to preserve
 * its existing behaviour. SignInPopover wraps this card without
 * `onEmailSent` so the card swaps in-place to a "Check your inbox"
 * panel on submit.
 *
 * Visual variants — see {@link SignInCardProps#variant}. `card`
 * (default) draws the standalone card chrome (border, shadow,
 * padding); `embedded` strips the chrome so the parent container
 * (e.g., the popover frame) provides its own.
 */

import type { FormEvent, JSX } from 'react'
import { GithubLogo } from './github_logo.tsx'
import { GoogleLogo } from './google_logo.tsx'
import { useSignInFlow, type UseSignInFlowProps } from './use_sign_in_flow.ts'
import styles from './sign_in_card.module.css'

/**
 * Props for {@link SignInCard}.
 *
 * Extends {@link UseSignInFlowProps} so the three flow callbacks
 * (`onContinueWithGithub`, `onContinueWithGoogle`, `onSubmitEmail`)
 * and `next` / `onEmailSent` live on a single surface.
 *
 * @property variant - `card` (default) draws the standalone card
 *   chrome. `embedded` strips chrome so a wrapping container can
 *   own the frame (popover, CTA section).
 * @property expired - When true, renders a "your session timed out"
 *   notice above the form. Used by `/authorize?expired=1`.
 * @property title - Card heading. Default: "Pull a chair up to the lab."
 * @property lede - Optional intro paragraph below the heading. Pass
 *   empty string or null to omit.
 * @property fineprint - Optional bottom-of-card fine print.
 * @property emailTtlLabel - Copy used by the "Check your inbox" panel
 *   for how long the link is good. Default `"15 minutes"`.
 */
export interface SignInCardProps extends UseSignInFlowProps {
  variant?: 'card' | 'embedded'
  expired?: boolean
  title?: string
  lede?: string | null
  fineprint?: string | null
  emailTtlLabel?: string
}

const DEFAULT_TITLE = 'Pull a chair up to the lab.'
const DEFAULT_LEDE =
  'LabCraft is open as a public beta. Pick your way in and start a lab or continue where you left off.'
const DEFAULT_FINEPRINT =
  'Already have an account? The same buttons sign you in.'

export function SignInCard(props: SignInCardProps): JSX.Element {
  const variant = props.variant ?? 'card'
  const title = props.title ?? DEFAULT_TITLE
  const lede = props.lede === undefined ? DEFAULT_LEDE : props.lede
  const fineprint =
    props.fineprint === undefined ? DEFAULT_FINEPRINT : props.fineprint
  const emailTtlLabel = props.emailTtlLabel ?? '15 minutes'

  const flow = useSignInFlow(props)

  return (
    <section
      className={
        variant === 'card'
          ? `${styles.surface} ${styles.surfaceCard}`
          : styles.surface
      }>
      <header className={styles.header}>
        <h1 className={styles.title}>{title}</h1>
        {lede && <p className={styles.lede}>{lede}</p>}
      </header>

      {props.expired && (
        <div className={styles.noticeBanner} role="status">
          Your session timed out. Sign back in to pick up where you left off.
        </div>
      )}

      {flow.mode === 'sent' ? (
        <SentPanel
          email={flow.sentEmail}
          ttlLabel={emailTtlLabel}
          onReset={flow.reset}
        />
      ) : (
        <FormBody flow={flow} flowProps={props} fineprint={fineprint} />
      )}
    </section>
  )
}

////////////////////////////////////////////////////////////////////
// Sub-components
////////////////////////////////////////////////////////////////////

function FormBody(props: {
  flow: ReturnType<typeof useSignInFlow>
  flowProps: UseSignInFlowProps
  fineprint: string | null
}): JSX.Element {
  const { flow, flowProps } = props
  const submitting = flow.mode === 'submitting'
  const hasGithub = Boolean(flowProps.onContinueWithGithub)
  const hasGoogle = Boolean(flowProps.onContinueWithGoogle)
  const hasEmail = Boolean(flowProps.onSubmitEmail)
  const hasSso = hasGithub || hasGoogle

  function onSubmitForm(e: FormEvent): void {
    void flow.onSubmit(e)
  }

  return (
    <>
      {flow.errorMessage && (
        <div className={styles.errorBanner} role="alert">
          {flow.errorMessage}
        </div>
      )}

      {hasSso && (
        <div className={styles.ssoStack}>
          {hasGithub && (
            <button
              type="button"
              className={styles.ssoButton}
              onClick={flow.onGithub}
              disabled={submitting}>
              <GithubLogo className={styles.ssoLogo} />
              Continue with GitHub
            </button>
          )}
          {hasGoogle && (
            <button
              type="button"
              className={styles.ssoButton}
              onClick={flow.onGoogle}
              disabled={submitting}>
              <GoogleLogo className={styles.ssoLogo} />
              Continue with Google
            </button>
          )}
        </div>
      )}

      {hasSso && hasEmail && <div className={styles.divider}>or</div>}

      {hasEmail && (
        <form className={styles.form} onSubmit={onSubmitForm} noValidate>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              className={styles.input}
              type="email"
              value={flow.email}
              onChange={e => flow.setEmail(e.target.value)}
              autoComplete="email"
              required
              placeholder="you@yourcompany.com"
            />
          </label>
          <button
            type="submit"
            className={styles.cta}
            disabled={!flow.canSubmit}>
            {submitting ? 'Sending…' : 'Email me a link →'}
          </button>
        </form>
      )}

      {props.fineprint && <p className={styles.fineprint}>{props.fineprint}</p>}
    </>
  )
}

function SentPanel(props: {
  email: string
  ttlLabel: string
  onReset: () => void
}): JSX.Element {
  return (
    <div className={styles.sentPanel} role="status" aria-live="polite">
      <p className={styles.sentEyebrow}>Check your inbox</p>
      <h2 className={styles.sentTitle}>One link, on its way.</h2>
      <p className={styles.sentBody}>
        We sent a link to <strong>{props.email}</strong>. Click it to sign in.
        The link is good for {props.ttlLabel}.
      </p>
      <p className={styles.fineprint}>
        Didn&apos;t arrive?{' '}
        <button
          type="button"
          className={styles.linkButton}
          onClick={props.onReset}>
          Send a new one
        </button>
      </p>
    </div>
  )
}
